import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError, timer } from 'rxjs';
import { mergeMap, retryWhen, shareReplay, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AdminUsuario } from '../models/admin-usuario';
import { AbstractService } from './abstract.service';

export type FlagRelatorio = 'V' | 'E' | 'I' | 'P' | 'D' | 'S';

export interface BaseRelatorio {
  cnpj: string;
  host?: string;
  empresas?: number[];
  descricao?: string;
}

export interface ClienteRelatorio {
  cnpj: string;
  nome?: string;
  banco?: string;
  servidor?: string;
  host?: string;
}

const CHAVE_BASE = 'relatorio-dinamico-base';

export interface ImportacaoJrxml {
  fonte: any;
  avisos: string[];
  sugestaoModelo: any;
  variaveis?: any[];
  origem: string;
}

export interface ColunaDicionario {
  nome: string;
  tipo: string;
  pk?: boolean;
  obrigatoria?: boolean;
  campo?: string;
  fk?: { tabela: string; coluna: string };
  enum?: { nome: string; gravadoComo: 'ordinal' | 'texto' };
  bloqueada?: boolean;
  descricao?: string;
}

export interface TabelaDicionario {
  nome: string;
  entidades?: string[];
  bloqueada?: boolean;
  descricao?: string;
  colunas: ColunaDicionario[];
}

export interface DicionarioSchema {
  versaoSchema: number;
  versaoSistema: string;
  geradoEm: string;
  tabelas: TabelaDicionario[];
  enums: { [nome: string]: { classe: string; valores: { ordinal: number; nome: string; rotulo?: string }[] } };
}

@Injectable({
  providedIn: 'root',
})
export class RelatorioDinamicoService {
  importacaoPendente: ImportacaoJrxml = null;
  base: BaseRelatorio = this.lerBase();
  private dicionario$: Observable<DicionarioSchema>;

  constructor(private http: HttpClient) {}

  definirBase(base: BaseRelatorio): void {
    this.base = base;
    try {
      localStorage.setItem(CHAVE_BASE, JSON.stringify(base));
    } catch {}
  }

  testarBase(base: { cnpj: string; host?: string }): Observable<any> {
    return this.post('admin/relatorio-base/testar', base);
  }

  dicionario(): Observable<DicionarioSchema> {
    if (!this.dicionario$) {
      this.dicionario$ = this.get<DicionarioSchema>('admin/schema-descricao/dicionario').pipe(
        retryWhen((erros) => erros.pipe(mergeMap((e, i) => (e?.status === 503 && i < 10 ? timer(3000) : throwError(e))))),
        tap({ error: () => (this.dicionario$ = null) }),
        shareReplay(1)
      );
    }
    return this.dicionario$;
  }

  buscarClientes(busca: string): Observable<ClienteRelatorio[]> {
    return this.get<ClienteRelatorio[]>('admin/relatorio-base/clientes?busca=' + encodeURIComponent(busca));
  }

  clientesPorCnpj(cnpjs: string[]): Observable<ClienteRelatorio[]> {
    return this.post<ClienteRelatorio[]>('admin/relatorio-base/clientes', { cnpjs });
  }

  pode(flag: FlagRelatorio): boolean {
    const usuario: AdminUsuario = AbstractService.getLocalStorage('usuario');
    if (!usuario) {
      return false;
    }
    const acessos: string = usuario.acessoRelatorios || '';
    if (acessos.includes('.' + flag + '.')) {
      return true;
    }
    return !!usuario.sysAdmin && flag !== 'S';
  }

  mensagemErro(e: any): string {
    if (e instanceof HttpErrorResponse) {
      const corpo = typeof e.error === 'string' ? this.tentaJson(e.error) : e.error;
      if (corpo?.erros?.length) {
        return corpo.erros.join(' ');
      }
      if (corpo?.erro) {
        return corpo.erro;
      }
      if (e.status === 0) {
        return 'Sem resposta do servidor.';
      }
      return e.message;
    }
    return e?.message || String(e);
  }

  corpoErro(e: any): any {
    if (e instanceof HttpErrorResponse) {
      return typeof e.error === 'string' ? this.tentaJson(e.error) : e.error;
    }
    return null;
  }

  categorias(): Observable<any[]> {
    return this.get('admin/relatorio-categoria');
  }

  salvarCategoria(categoria: any): Observable<any> {
    return this.post('admin/relatorio-categoria', categoria);
  }

  excluirCategoria(id: number): Observable<any> {
    return this.http.delete(this.url('admin/relatorio-categoria/' + id), { headers: this.headers() });
  }

  fontes(): Observable<any[]> {
    return this.get('admin/relatorio-fonte');
  }

  fonte(id: number): Observable<any> {
    return this.get('admin/relatorio-fonte/' + id);
  }

  criarFonte(fonte: any): Observable<any> {
    return this.post('admin/relatorio-fonte', { fonte });
  }

  salvarRascunhoFonte(id: number, fonte: any): Observable<any> {
    return this.http.put(this.url(`admin/relatorio-fonte/${id}/rascunho`), { fonte }, { headers: this.headers() });
  }

  descartarRascunhoFonte(id: number): Observable<any> {
    return this.post(`admin/relatorio-fonte/${id}/descartar-rascunho`, {});
  }

  salvarVersaoFonte(id: number, fonte: any, notas: string): Observable<any> {
    return this.post(`admin/relatorio-fonte/${id}/versao`, { fonte, notas, base: this.corpoBase() });
  }

  excluirFonte(id: number): Observable<any> {
    return this.http.delete(this.url('admin/relatorio-fonte/' + id), { headers: this.headers() });
  }

  versoesFonte(id: number): Observable<any[]> {
    return this.get(`admin/relatorio-fonte/${id}/versoes`);
  }

  versaoFonte(id: number, versao: number): Observable<any> {
    return this.get(`admin/relatorio-fonte/${id}/versoes/${versao}`);
  }

  validarFonte(fonte: any): Observable<any> {
    return this.post('admin/relatorio-fonte/validar', { fonte });
  }

  detectarColunas(fonte: any): Observable<any> {
    return this.post('admin/relatorio-fonte/detectar-colunas', { fonte, base: this.corpoBase() });
  }

  previaFonte(fonte: any, limite = 50, filtros: any = null, pagina = 0): Observable<any> {
    return this.post('admin/relatorio-fonte/previa', { fonte, limite, pagina, filtros, base: this.corpoBase() });
  }

  planoExecucao(fonte: any): Observable<any> {
    return this.post('admin/relatorio-fonte/plano-execucao', { fonte, base: this.corpoBase() });
  }

  importarJrxml(arquivo: File): Observable<ImportacaoJrxml> {
    const form = new FormData();
    form.append('arquivo', arquivo, arquivo.name);
    return this.http.post<ImportacaoJrxml>(this.url('admin/relatorio-fonte/importar-jrxml'), form, {
      headers: new HttpHeaders({ 'X-Auth-Token': AbstractService.token() }),
    });
  }

  modelos(): Observable<any[]> {
    return this.get('admin/relatorio-modelo');
  }

  modelo(id: number): Observable<any> {
    return this.get('admin/relatorio-modelo/' + id);
  }

  fontesParaModelo(): Observable<any[]> {
    return this.get('admin/relatorio-modelo/fontes');
  }

  schema(): Observable<any> {
    return this.get('admin/relatorio-modelo/schema');
  }

  criarModelo(modelo: any, categoriaId: number): Observable<any> {
    return this.post('admin/relatorio-modelo', { modelo, categoriaId });
  }

  salvarRascunhoModelo(id: number, modelo: any, categoriaId: number): Observable<any> {
    return this.http.put(this.url(`admin/relatorio-modelo/${id}/rascunho`), { modelo, categoriaId }, { headers: this.headers() });
  }

  validarModelo(modelo: any): Observable<any> {
    return this.post('admin/relatorio-modelo/validar', { modelo });
  }

  previaModelo(modelo: any, filtros: any, fonte: any = null): Observable<any> {
    return this.post('admin/relatorio-modelo/previa', { modelo, filtros, fonte, base: this.corpoBase() });
  }

  proporIa(modo: 'criar' | 'ajustar', mensagens: { papel: string; texto: string }[], modelo: any = null, fonte: any = null): Observable<any> {
    return this.post('admin/relatorio-ia/propor', { modo, mensagens, modelo, fonte, base: this.corpoBase() });
  }

  salvarDistribuicao(id: number, distribuicao: any): Observable<any> {
    return this.http.put(this.url(`admin/relatorio-modelo/${id}/distribuicao`), distribuicao, { headers: this.headers() });
  }

  publicar(id: number, piloto: boolean, cnpjsPiloto: string[], notas: string): Observable<any> {
    return this.post(`admin/relatorio-modelo/${id}/publicar`, { piloto, cnpjsPiloto, notas });
  }

  abrirParaTodos(id: number): Observable<any> {
    return this.post(`admin/relatorio-modelo/${id}/abrir-para-todos`, {});
  }

  encerrarPiloto(id: number): Observable<any> {
    return this.post(`admin/relatorio-modelo/${id}/encerrar-piloto`, {});
  }

  republicar(id: number, versao: number): Observable<any> {
    return this.post(`admin/relatorio-modelo/${id}/republicar/${versao}`, {});
  }

  arquivar(id: number, arquivado: boolean): Observable<any> {
    return this.post(`admin/relatorio-modelo/${id}/arquivar`, { arquivado });
  }

  excluirModelo(id: number): Observable<any> {
    return this.http.delete(this.url('admin/relatorio-modelo/' + id), { headers: this.headers() });
  }

  versoesModelo(id: number): Observable<any[]> {
    return this.get(`admin/relatorio-modelo/${id}/versoes`);
  }

  versaoModelo(id: number, versao: number): Observable<any> {
    return this.get(`admin/relatorio-modelo/${id}/versoes/${versao}`);
  }

  rotinas(): Observable<{ value: string; description: string }[]> {
    return this.get('registro/produto/acessos');
  }

  private corpoBase(): any {
    if (!this.base?.cnpj) {
      return null;
    }
    return { cnpj: this.base.cnpj, host: this.base.host || null, empresas: this.base.empresas || [] };
  }

  private lerBase(): BaseRelatorio {
    try {
      return JSON.parse(localStorage.getItem(CHAVE_BASE) || 'null');
    } catch {
      return null;
    }
  }

  private get<T = any>(caminho: string): Observable<T> {
    return this.http.get<T>(this.url(caminho), { headers: this.headers() });
  }

  private post<T = any>(caminho: string, corpo: any): Observable<T> {
    return this.http.post<T>(this.url(caminho), corpo, { headers: this.headers() });
  }

  private url(caminho: string): string {
    return environment.api + '/' + caminho;
  }

  private headers(): HttpHeaders {
    return new HttpHeaders({
      Accept: 'application/json; charset=utf-8',
      'Content-Type': 'application/json',
      'X-Auth-Token': AbstractService.token(),
    });
  }

  private tentaJson(texto: string): any {
    try {
      return JSON.parse(texto);
    } catch {
      return { erro: texto };
    }
  }
}
